import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useMutation } from 'react-relay';
import { ButtonItem, Section } from '@atlaskit/menu';
import { fg } from '@atlassian/jira-feature-gating';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { useVersionIssuesGraphQLVariables } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/graphql-variables/index.tsx';
import {
	useRemoveIssueFromVersionExperienceStart,
	useRemoveIssueFromVersionExperienceFail,
	useRemoveIssueFromVersionExperienceSuccess,
} from '../../../services/remove-issues-experience-tracker/index.tsx';
import type { removeIssueFromVersion_softwareReleasesMoveIssuesBetweenVersionsMutation } from './__generated__/removeIssueFromVersion_softwareReleasesMoveIssuesBetweenVersionsMutation.graphql';
import type { removeIssueFromVersion_softwareReleasesMoveIssuesBetweenVersions$key } from './__generated__/removeIssueFromVersion_softwareReleasesMoveIssuesBetweenVersions.graphql';
import messages from './messages.tsx';
import { getErrorMessage } from './utils.tsx';

type Props = {
	jiraIssue: removeIssueFromVersion_softwareReleasesMoveIssuesBetweenVersions$key;
	originalJiraVersion: string;
};

export const RemoveIssueFromVersion = ({
	jiraIssue,
	originalJiraVersion,
}: Props): React.JSX.Element => {
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const issue = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment removeIssueFromVersion_softwareReleasesMoveIssuesBetweenVersions on JiraIssue {
				id @required(action: THROW)
				key @required(action: THROW)
			}
		`,
		jiraIssue,
	);

	const [removeIssuesFromFixVersion] =
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		useMutation<removeIssueFromVersion_softwareReleasesMoveIssuesBetweenVersionsMutation>(graphql`
			mutation removeIssueFromVersion_softwareReleasesMoveIssuesBetweenVersionsMutation(
				$input: JiraRemoveIssuesFromFixVersionInput!
				$sortBy: JiraVersionIssuesSortInput
				$filters: JiraVersionIssuesFiltersInput
				$isDevopsFeatureDisabledInFedRamp: Boolean!
				$shouldIncludeDevOpsFieldsViaAgs: Boolean!
			) {
				jira {
					removeIssuesFromFixVersion(input: $input) {
						success
						errors {
							message
						}
						issuesWithMissingResolvePermission
						issuesWithMissingEditPermission
						version {
							...addRelatedWorkPopup_softwareReleasesAddRelatedWorkToVersion_jiraVersion
							...issueTotalCount_softwareReleasesVersionDetailIssueList
							...ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion
							...ui_softwareReleasesVersionDetailIssueList_IssuesRefetchable_jiraVersion
						}
					}
				}
			}
		`);
	const { filters, sortBy } = useVersionIssuesGraphQLVariables();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});

	const experienceStart = useRemoveIssueFromVersionExperienceStart();
	const experienceFail = useRemoveIssueFromVersionExperienceFail();
	const experienceSuccess = useRemoveIssueFromVersionExperienceSuccess();

	const onError = (err: Error) => {
		showFlag({
			type: 'error',
			title: formatMessage(messages.errorFlagDescription, {
				issueKey: issue.key,
			}),
			messageId:
				'software-releases-move-issues-between-versions.ui.version-list.remove-issue-from-version.show-flag.error.on-error.show-flag.error',
			messageType: 'transactional',
		});
		experienceFail('graphql error', err);
		fireErrorAnalytics({
			error: err,
			meta: {
				id: 'jira.releases.remove-issue-from-version.mutation',
				packageName: 'jiraSoftwareReleasesMoveIssuesBetweenVersions',
				teamName: 'jolt',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const onErrorWithPermissionValidationEnabled = (
		issueKey: string,
		issuesWithMissingResolvePermission: readonly string[] | undefined | null,
		issuesWithMissingEditPermission: readonly string[] | undefined | null,
	) => {
		const error = getErrorMessage(
			issueKey,
			issuesWithMissingResolvePermission,
			issuesWithMissingEditPermission,
			formatMessage,
		);

		showFlag({
			type: 'error',
			title: formatMessage(
				fg('jira-issue-terminology-update-m4')
					? messages.errorFlagTitleIssueTerminologyRefreshM4
					: messages.errorFlagTitle,
			),
			description: error,
			messageId:
				'software-releases-move-issues-between-versions.ui.version-list.remove-issue-from-version.show-flag.error.on-error-with-permission-validation-enabled.show-flag.error',
			messageType: 'transactional',
		});
		experienceFail('graphql error', new Error(error));
		fireErrorAnalytics({
			error: new Error(error),
			meta: {
				id: 'jira.releases.remove-issue-from-version.mutation',
				packageName: 'jiraSoftwareReleasesMoveIssuesBetweenVersions',
				teamName: 'jolt',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const onUpdateVersion = () => {
		removeIssuesFromFixVersion({
			variables: {
				input: {
					issueIds: [issue.id],
					versionId: originalJiraVersion,
				},
				isDevopsFeatureDisabledInFedRamp: isDevopsFeatureDisabledInFedRamp(),
				shouldIncludeDevOpsFieldsViaAgs: !isDevopsFeatureDisabledInFedRamp(),
				filters,
				sortBy,
			},
			onCompleted: (data) => {
				if (data?.jira?.removeIssuesFromFixVersion?.success !== true) {
					const issuesWithMissingResolvePermission =
						data?.jira?.removeIssuesFromFixVersion?.issuesWithMissingResolvePermission || [];
					const issuesWithMissingEditPermission =
						data?.jira?.removeIssuesFromFixVersion?.issuesWithMissingEditPermission || [];

					onErrorWithPermissionValidationEnabled(
						issue.key,
						issuesWithMissingResolvePermission,
						issuesWithMissingEditPermission,
					);
				} else {
					const flagText = formatMessage(messages.successFlagDescription, {
						issueKey: issue.key,
					});
					experienceSuccess();
					showFlag({
						type: 'success',
						title: flagText,
						messageId:
							'software-releases-move-issues-between-versions.ui.version-list.remove-issue-from-version.show-flag.success.on-completed.show-flag.success',
						messageType: 'transactional',
					});
				}
			},
			onError,
		});
	};

	return (
		<Section hasSeparator>
			<ButtonItem
				onClick={() => {
					fireUIAnalytics(analyticsEvent, 'removeIssueFromVersion');
					experienceStart();
					onUpdateVersion();
				}}
			>
				{formatMessage(messages.removeButtonTitle)}
			</ButtonItem>
		</Section>
	);
};
